import $ from 'jquery'
import get from 'lodash/get'
import has from 'lodash/has'
import includes from 'lodash/includes'
import memoize from 'lodash/memoize'

import type { ConfigLayerOptions } from './ConfigLayer'

import { HistoryUpdate } from 'api/history'
import * as Model from 'api/model'
import PoziAPISearch from 'api/search'
import * as PoziUtils from 'api/utils'
import { hideLoadingScreen, progressBarIncreaseMax, progressBarIncreaseValue } from 'app/app'
import type { ConfigLayerCapabilityOptions } from 'config/catalog/ConfigLayerCapability'
import type { ConfigLayerWMTSOptions } from 'config/catalog/ConfigLayerWMTS'
import type ConfigSite from 'config/ConfigSite'
import { MapLayer } from 'config/MapLayer'
import { MapLayerCapabilities } from 'config/MapLayerCapabilities'
import type { ConfigLayerEsriXyzOptions } from 'config/MapLayerEsriXyz'
import { MapLayerEsriXyz } from 'config/MapLayerEsriXyz'
import { MapLayerFolder } from 'config/MapLayerFolder'
import { MapLayerGeoJson } from 'config/MapLayerGeoJson'
import { MapLayerGoogleMaps } from 'config/MapLayerGoogleMaps'
import type { ConfigLayerImageWMSOptions } from 'config/MapLayerImageWMS'
import { MapLayerImageWMS } from 'config/MapLayerImageWMS'
import { MapLayerMapBox } from 'config/MapLayerMapBox'
import { MapLayerNone } from 'config/MapLayerNone'
import type { ConfigLayerTileWMSOptions } from 'config/MapLayerTileWMS'
import { MapLayerTileWMS } from 'config/MapLayerTileWMS'
import { MapLayerTileWMSAuth } from 'config/MapLayerTileWMSAuth'
import { MapLayerVectorTile } from 'config/MapLayerVectorTile'
import type { ConfigLayerVicMapOptions } from 'config/MapLayerVicMap'
import { MapLayerVicMap } from 'config/MapLayerVicMap'
import { MapLayerWMTS } from 'config/MapLayerWMTS'
import { MapLayerXyzTile } from 'config/MapLayerXyzTile'
import { doMsal, getAccount, getAccountGroups } from 'config/msalUtils'
import GeoJSONIO from 'data/geojsonio'
import ResourceCheck from 'data/ResourceCheck'
import PoziDrawer from 'drawer/drawer'
import PoziFeedback from 'drawer/tabs/info/feedback'
import { ShowWhatsHereFeatureFromURL } from 'drawer/tabs/info/WhatsHereUtils'
import BuildKeywords from 'drawer/tabs/layers/search/BuildKeywords'
import { applyAutoFilterToVisibleLayers, setCurrentLevel } from 'drawer/tabs/layers/utils/autoFilterOnLevel'
import fullScreen from 'utils/Fullscreen'
import { AutoSelectFeatureFromURL } from 'utils/MapUtils'
import PoziNotifications from 'utils/notifications'

import { ConfigLayerType } from './ConfigLayerType'

// This is used to check if the browser supports the full screen API
const fullScreenIsSupported = 'exitFullscreen' in document // this will be missing in Safari on iPhone

export const createOlLayerFromLayerConf = async (layerConf: ConfigLayerCapabilityOptions | ConfigLayerOptions) => {
  const layerType: string = layerConf.type
  const layerTypeLC: string = layerType.toLowerCase()

  // console.debug('createOlLayerFromLayerConf', layerConf.titleid, layerTypeLC, layerConf)

  // Ignore excluded layers
  const excludeTypes = ['image', 'search']
  if (excludeTypes.includes(layerTypeLC)) {
    // console.debug('createOlLayerFromLayerConf - no action. Layer is excluded:', layerConf.titleid, layerTypeLC)
    return
  }

  let olLayer: any

  switch (layerTypeLC) {
    // ArcGIS XYZ
    case 'esrixyz':
      olLayer = MapLayerEsriXyz.createLayer(layerConf as ConfigLayerEsriXyzOptions)
      break

    // Folder
    case 'folder':
      olLayer = MapLayerFolder.createLayer(layerConf)
      break

    // GeoJSON: most of our vector layers
    case 'geojson':
      olLayer = MapLayerGeoJson.createLayer(layerConf)
      break

    // Google Maps
    case 'googlemaps':
      olLayer = MapLayerGoogleMaps.createLayer(layerConf)
      break

    // Single Image WMS
    case 'imagewms':
      olLayer = MapLayerImageWMS.createLayer(layerConf as ConfigLayerImageWMSOptions)
      break

    // Mapbox / Maplibre / Maptiler style layers
    case 'mapbox':
    case 'mapbox-gl':
    case 'mapbox-olms':
    case 'mapbox-olsm': // left-over from a typo. Remove when all layers are updated
    case 'maplibre-gl-arcgis':
    case 'maplibre-gl':

      olLayer = MapLayerMapBox.createLayer(layerConf)
      break

    // TileWMS
    case 'tilewms':
      olLayer = MapLayerTileWMS.createLayer(layerConf as ConfigLayerTileWMSOptions)
      break

    // TileWMSAuth
    case 'tilewmsauth':
      olLayer = MapLayerTileWMSAuth.createLayer(layerConf as ConfigLayerTileWMSOptions)
      break

    // VectorTile
    case 'vectortile':
      olLayer = MapLayerVectorTile.createLayer(layerConf)
      break

    // VicMapAPI. Note group name must be 'Basemap' for exclusive section to work.
    case 'vicmap':
      olLayer = MapLayerVicMap.createLayer(layerConf as ConfigLayerVicMapOptions)
      break

    // WMTS
    case 'wmts':
      olLayer = MapLayerWMTS.createLayer(layerConf as ConfigLayerWMTSOptions)
      break

    // XYZ
    case 'xyz':
      olLayer = MapLayerXyzTile.createLayer(layerConf as ConfigLayerTileWMSOptions)
      break

    // Capabilities Layers (QGISProjectSettings/WFS/WMS/WMTS)
    case ConfigLayerType.QGIS_PROJECT_SETTINGS.toLowerCase():
    case ConfigLayerType.WFS_GET_CAPABILITIES.toLowerCase():
    case ConfigLayerType.WMS_GET_CAPABILITIES.toLowerCase():
    case ConfigLayerType.WMTS_CAPABILITIES.toLowerCase(): {
      progressBarIncreaseMax()
      olLayer = await MapLayerCapabilities.createLayer(layerConf as ConfigLayerCapabilityOptions)
      progressBarIncreaseValue()
      break
    }

    // Unsupported type 'none'
    case 'none':
      olLayer = MapLayerNone.createLayer(layerConf)
      console.warn('createOlLayerFromLayerConf - unsupported layer type', layerTypeLC, layerConf.titleid)
      break
  }

  if (!olLayer) {
    console.warn('createOlLayerFromLayerConf unknown layer', layerType, layerConf.titleid)
    PoziNotifications().warning(`Something went wrong: layer may be misconfigured. Layer: '${layerConf.title}', type: '${layerConf.type}'`)
  } else {
    // console.debug('createOlLayerFromLayerConf new layer', layerTypeLC, layerConf.titleid)
  }

  return olLayer
}

/**
 * Define config for layers and modes.
 * Load configuration from JSON, enrich and interpret
 * Then call the initialisation of map and tabs.
 */
const PoziConfig = memoize(function (this: any) {
  const app = PoziUtils.getApp()

  const __ = this
  // const parent

  // Template config files
  __.rawSiteConfig = {}

  // Local Data Source Config Found
  __.localDataSourceConfigFound = false

  // Site Name
  __.siteName = 'public'

  /**
   * Test for local data source.
   */
  __.testForLocalDataSource = async function () {
    console.debug('PoziConfig - testForLocalDataSource...')

    if (!has(__.rawSiteConfig, 'localDataSource.PoziServerURL')) {
      console.log('PoziConfig - Local data source config not found. Skip check for local data source.')
      // __.decodeConfig()
      return
    }

    const PoziServerURL = get(__.rawSiteConfig, 'localDataSource.PoziServerURL', '')

    let isPoziServerURLHTTPS = false
    if (includes(PoziServerURL, 'https:')) {
      isPoziServerURLHTTPS = true
    }

    let isCurrentURLHTTPS = false
    if (includes(window.location.protocol, 'https:')) {
      isCurrentURLHTTPS = true
    }

    // Check for a PoziServerURL that is not https
    if (!isPoziServerURLHTTPS && isCurrentURLHTTPS) {
      console.warn('PoziConfig - Skip checking local data source since PoziServer URL is http and current url is https.')
      return
    }

    // Uncomment to test privacy policy (and use incognito mode on Chrome)
    // url = 'http://localhost:3000/favicon.ico?d=' + new Date().getTime()

    console.debug('PoziConfig - Async check for local data source. (favicon.ico 404 or CORS is not an error) do not wait to start rendering config...')

    // Browser refresh will be required to see this. But decision is that we dont want to wait before rendering.
    // Added timeout to remove jQuery failure message. a few seconds seems like an eon of time anyway.
    // Seems like setting this above 10 seconds jquery just exits anyway.
    console.debug('PoziConfig - Testing for PoziServer with URL', PoziServerURL)

    let bUrlExists = false
    let requestErrorObj = null
    try {
      bUrlExists = await ResourceCheck(PoziServerURL, undefined, true)
    } catch (error: unknown) {
      console.log('PoziConfig - testForLocalDataSource - request error', error)

      const jqxhr = error as JQuery.jqXHR // Make TypeScript happy

      const { status, statusText } = jqxhr

      // Deconstruct response headers that get returned as a string from jQuery ¯\_(ツ)_/¯
      // From: https://stackoverflow.com/a/53977821
      // TODO: move to api/data.js
      const getResponseHeaders = (jqxhr: JQuery.jqXHR) => {
        const responseHeadersString = jqxhr.getAllResponseHeaders() || ''

        if (!responseHeadersString) {
          return null
        }

        const headers: Record<string, string> = {}
        responseHeadersString
          .trim()
          .split(/[\r\n]+/)
          .map(value => value.split(/: /))
          .forEach(([key, value = '']) => {
            // console.debug('PoziConfig - testForLocalDataSource - getResponseHeaders - key / value', key, value)

            headers[key.trim()] = value.trim()
          })

        return headers
      }

      const responseHeaders = getResponseHeaders(jqxhr)

      requestErrorObj = {
        responseHeaders,
        status,
        statusText
      }

      console.log('PoziConfig - testForLocalDataSource - requestErrorObj', requestErrorObj)
    }

    console.debug('PoziConfig - testForLocalDataSource - bUrlExists', bUrlExists)

    if (bUrlExists) {
      Model.set('app.LocalDataSourceDetected', true)

      PoziNotifications()
        .privacy()
        .then(function (msg: any) {
          console.debug('PoziConfig - PoziNotifications', msg)
        })
        .catch(function (err: Error) {
          console.error(err)
        })
    } else {
      console.debug('PoziConfig - testForLocalDataSource - PoziServer not found', PoziServerURL)

      // If the site is enterprise, then notify the user that they cannot connect to the internal data sources
      const appInfo = window.getAppInfo()
      const isEnterprise = appInfo.enterprise

      if (isEnterprise) {
        // @ts-expect-error
        const isLoggedIn = !!(window.msalInstance && window.msalInstance.getActiveAccount())

        // If error is MS App Proxy related, then show it
        const appProxyErrorDetails = requestErrorObj?.responseHeaders?.['x-ms-proxy-error-details']

        const errorMessage = appProxyErrorDetails ? `Error message: ${appProxyErrorDetails}` : ''

        console.log('PoziConfig - testForLocalDataSource - app proxy error', appProxyErrorDetails, requestErrorObj, errorMessage)

        let message = ''

        // Handle errors
        const { status } = requestErrorObj || {}
        if (status === 404) {
          message = `Page can't be found: ${PoziServerURL}`
        } else if (isLoggedIn) {
          message = `${errorMessage}`
        } else {
          message = `Authentication is required. ${errorMessage}`
        }

        message = `Cannot connect to internal data sources. Please reload page. If the problem persists, report this issue to your site administrator. ${message}`

        PoziNotifications().error(message, {
          autoHideDuration: null // disable auto hide
        })
      }
    }
  }

  /**
   * Fetch and process the config
   */
  __.start = async function (siteName: string) {
    __.siteName = siteName

    // Now managed by Model()
    // _getSiteConfig()
    __.rawSiteConfig = Model.get('config') // TODO: Replace all references with Model.get('config')
    app.siteConfig = __.rawSiteConfig // TODO: Replace all references with Model.get('config')

    const configIsValid = testConfigIsValidJSON()
    if (!configIsValid) {
      PoziNotifications().error('Site configuration is not valid. Please report this issue to the site administrator.', {
        autoHideDuration: null // disable auto hide
      })
      return
    }

    updateHeaderHTML()
    PoziFeedback().init()

    // MS Azure AD setup: run after config is loaded to use
    // azure settings from it
    await doMsal()

    console.debug('PoziConfig - start - Check for local data source ...')
    await __.testForLocalDataSource()

    // Initialise Google Analytics (V4) after we have set the page title
    // and know whether the user access is public or authenticated
    PoziUtils.gtagInit()

    setPageTitle()

    // Load config + layers
    console.debug('PoziConfig - start - Fetch and process layer config ...')
    await GeoJSONIO().getLayerConfig()

    // await PoziUtils.sleep(5000) // for testing purposes

    removeRestrictedLayers()
    removeDisabledLayers()
    // await addCatalogLayers() // Disable this when Cap parsing is working in buildLayers

    console.debug('PoziConfig - start - Building layers ...')
    await buildLayers()

    // This currently breaks showing the search layers.
    // TODO: do we actually need this like this? If anything we'd need to
    //       sort the z-index for OL layers
    // sortLayers()

    hideLoadingScreen()

    showElements()
    app.mapDivResize() // Resizing the display

    console.debug('PoziConfig - start - Init map ...')
    await app.poziMap.initMap()

    // Due to timing issues, we wait a short while for all URL params to have been
    // process and layers to have been configured before we initialise the app
    // TODO: one day, fix the acutual issue
    await PoziUtils.sleep(200)

    console.debug('PoziConfig - start - Init app...')

    initApp()

    ShowWhatsHereFeatureFromURL()

    AutoSelectFeatureFromURL()

    const currentLevel = determineCurrentLevel()
    if (currentLevel) {
      setCurrentLevel(currentLevel)
      applyAutoFilterToVisibleLayers(currentLevel)
    }

    // Auto-open print PDF UI (for dev purposes)
    // window.setTimeout(async () => {
    //   const printUIOpen = (await import('utils/Print/actions')).printUIOpen

    //   window.store.dispatch(printUIOpen())
    // }, 2000)
  }

  /**
   * Determine which dataset in the 'levels' group is currently displayed
   *
   * @return {layer|null}
   */
  const determineCurrentLevel = function () {
    const visibleLevels = Object.entries(Model.get('app.config.layerbytitleid', {})) // convert obj to array
      // FIXME: Properly type the Object.entries so that the filter does not complain below!
      // @ts-ignore
      .filter(([key, value]) => value.visible) // keep visible layers
      .map(([key, value]) => PoziUtils.getLayerFromTitleID(key)) // get the full layer
      // @ts-ignore
      .filter((layer: ConfigLayerOptions) => layer.groupid === 'levels') // keep the levels

    if (visibleLevels.length === 0) {
      // assume that there is no 'levels' group, or that the config has an error
      return null
    }

    if (visibleLevels.length > 1) {
      // this should not happen, but we will continue anyway
      console.error('There is more than one dataset in the exclusive group ' + "'levels' that is currently visible.")
    }

    return visibleLevels[0]
  }

  const testConfigIsValidJSON = function () {
    let configIsValid = false

    try {
      JSON.parse(JSON.stringify(__.rawSiteConfig))

      configIsValid = true
    } catch (error) {
      const errorMsg = 'Config invalid for site ' + Model.get('app.site', '')

      PoziUtils.gtag({
        category: 'program',
        status: 'warning',
        identifier: 'Pozi',
        action: errorMsg,
        additionalInfo: errorMsg
      })
      console.log('PoziConfig - Config invalid!', error)
    }

    return configIsValid
  }

  const setPageTitle = function () {
    const { title, titleInternal } = Model.get('config')
    const isAuthenticated = Model.get('app.LocalDataSourceDetected', false)

    // If an internal title is available and the user is authenticated, show that
    // instead of the default title
    const documentTitle = (isAuthenticated && titleInternal) ? titleInternal : title

    document.title = documentTitle || 'Pozi Web Map'
  }

  const updateHeaderHTML = function () {
    // Injecting the logo image and links
    let logoURL = Model.get('config.layout.favicon', '')?.toString()
    if (logoURL === '') {
      logoURL = Model.get('config.layout.logo', '')?.toString()
    }
    let bigLogoURL = Model.get('config.layout.mapicon', '')?.toString()
    if (bigLogoURL === '') {
      bigLogoURL = Model.get('config.layout.biglogo', '')?.toString()
    }

    if (bigLogoURL === '') {
      $('#logoDiv img').hide()
    } else {
      $('#logoDiv img').attr('src', bigLogoURL)
    }
    $('#favicon').attr('href', logoURL)

    const siteURL = Model.get('config.layout.url', '')?.toString()
    $('#logoDivSiteLink').attr('href', siteURL)
    $('#logoDivSiteLink').attr('target', '_blank')
    $('#logoDivSiteLink').attr('alt', siteURL)
    $('#logoDivSiteLink').attr('title', siteURL)
  }

  // Loop through all layers and generate a list of groups
  const getGroupNameSet = (configSite: ConfigSite): Set<string> => {
    const groupNameSet = new Set<string>()
    configSite.layers.forEach(layer => {
      // Ignore layers that do no show up in the layer control
      if (!layer.showInLayerControl) {
        return
      }

      // console.debug('PoziConfig - updateHeaderHTML - getGroupNameSet - add layer', layer.group, layer.titleid)

      groupNameSet.add(layer.group)
    })

    // console.debug('PoziConfig - updateHeaderHTML - getGroupNameSet: ', groupNameSet)

    return groupNameSet
  }

  const removeRestrictedLayers = () => {
    const appInfo = window.getAppInfo()
    const { enterprise } = appInfo

    const msalUser = enterprise && getAccount()
    const msalUserGroups = enterprise && getAccountGroups()

    // Determine if we're authenticated or not
    const bLocalDataSourceDetected = Model.get('app.LocalDataSourceDetected', false) as boolean

    // console.debug('removeRestrictedLayers - enterprise/user/groups: ', enterprise, msalUser, msalUserGroups, bLocalDataSourceDetected)

    // In the future we may have other ways to check if the user has access
    const userIsAuthenticated = bLocalDataSourceDetected

    // Respect localDataSource setting when not authenticated and remove those sources
    __.rawSiteConfig.layers = __.rawSiteConfig.layers.filter((layerConf: ConfigLayerOptions) => {
      const layerRestricted = layerConf.localDataSource ?? false
      if (layerRestricted && !userIsAuthenticated) {
        console.debug(`PoziConfig - removeRestrictedLayers - layer is restricted: '${layerConf.titleid}'. Removing. Reason: user not authenticated`)

        return false
      }

      // If layer is restricted to certain groups (through entra id), then make sure that the user
      // is in one of those groups
      let layerReadAccessGroups: string[] = []

      // TODO: do the conversion from string to array somewhere else, when parsing the config
      if (typeof layerConf.readAccessGroups === 'string') {
        layerReadAccessGroups = layerConf.readAccessGroups.replace(/\s+/g, '').split(',')
        // console.debug('removeRestrictedLayers - layerConf.readAccessGroups', layerConf.readAccessGroups)
      }

      const layerHasReadAccessGroups = layerReadAccessGroups.length > 0
      if (layerHasReadAccessGroups) {
        // User has no groups, therefore should not have access to this group restricted layer
        if (!msalUserGroups) {
          console.debug(`removeRestrictedLayers - layer is group restricted: '${layerConf.titleid}'. Removing. Reason: no user groups found.`)

          return false
        }

        // console.debug('removeRestrictedLayers - layer is group restricted', layerConf.titleid, layerReadAccessGroups, msalUserGroups)

        // Compile a list of groups within the user account that grant the user access to the layer
        const validGroups = layerReadAccessGroups.filter((group: string) => msalUserGroups.includes(group))

        const userHasValidGroup = validGroups.length > 0
        if (!userHasValidGroup) {
          console.debug(
            `removeRestrictedLayers - layer is group restricted: '${layerConf.titleid}'. Removing. Reason: no valid group id found.`,
            layerReadAccessGroups,
            msalUserGroups
          )

          return false
        }

        console.debug('removeRestrictedLayers - layer is group restricted and user has access to it', layerConf.titleid, validGroups)
      } else {
        // console.log(`removeRestrictedLayers - layer is not group restricted: '${layerConf.titleid}'.`)
      }

      return true
    })
  }

  const buildLayers = async function () {
    // Layers array will contain fully configured layer
    __.rawSiteConfig.layersOL3 = []

    // Translate JSON config into an OL Layer
    const layerConfs = [...__.rawSiteConfig.layers] // use a copy, just in case the array changes mid-way

    // Request all layer confs asynchronously by creating
    // an array of layerConfs and olLayer promises
    const results = await Promise.all(
      layerConfs.map((layerConf) => {
        const olLayerPromise = createOlLayerFromLayerConf(layerConf)

        return [layerConf, olLayerPromise]
      })
    )

    // Create Set of layerConfs and batch add them to redux later. We will always
    // add the parent layerConf first. Set will guarantee no duplicates
    // This will speed up the app initialisation
    const reduxLayerConfs: Set<ConfigLayerOptions> = new Set()

    // Now all results have been fetched, loop over them in the original order
    // and register the layers
    for (const [layerConf, olLayerPromise] of results) {
      const { group, showInLayerControl, title, titleid } = layerConf

      // Handle sitation where a layer has no defined group. We give it a group with
      // the same name as the layer title but only if it is set to show in the layerControl
      if (!group && showInLayerControl) {
        layerConf.group = title
        layerConf.groupid = titleid
      }

      // Get the resulting value from the promise
      const olLayer = await olLayerPromise

      // Some data sources don't return OL layers (like address/search)
      if (!olLayer) {
        // console.debug('buildLayers - No OL layer', titleid)

        MapLayer.registerInRedux(layerConf)
        continue
      }

      // Register layer
      MapLayer.registerLayer(layerConf, olLayer, reduxLayerConfs)

      // Generate keywords (used by LayerSearch) and store in layerConf as 'filter'
      // Note: BuildKeywords needs 'config.layers' and needs to be run after all layers
      //       and child layers have been registered.
      BuildKeywords(layerConf, olLayer)
    }

    // Add layerConfs to redux store
    MapLayer.registerInRedux(Array.from(reduxLayerConfs))
  }

  // Do not process layer if set to not be enabled
  const removeDisabledLayers = () => {
    const disabledLayers: string[] = []

    __.rawSiteConfig.layers = __.rawSiteConfig.layers.filter((layerConf: any) => {
      const layerEnabled = layerConf.enabled ?? true
      if (!layerEnabled) {
        disabledLayers.push(layerConf.titleid)
      }

      return layerEnabled
    })

    if (disabledLayers.length > 0) {
      console.debug('removeDisabledLayers - layers not enabled and therefore removed: ', disabledLayers)
    }
  }

  // Loop through the WMS/WFS Get Capabilities layers to fetch the layers that are available at each endpoint
  // We may need to alter this code when we do async loading of layers
  const sortLayers = () => {
    const groupNameSet = getGroupNameSet(__.rawSiteConfig)
    const sortedGroupLayers: ConfigLayerOptions[] = []
    groupNameSet.forEach(group => {
      // Get the layers that belong to each Group in Order of Set
      const groupLayers = __.rawSiteConfig.layers.filter((layer: ConfigLayerOptions) => {
        return layer.group === group
      })
      // Push the layers into the layers array sorted by group name
      groupLayers.forEach((groupLayer: ConfigLayerOptions) => {
        sortedGroupLayers.push(groupLayer)
      })
    })
    // Replace the Layers in the Site Config with the Layers Sorted by the order they appear in the initial Group Set
    __.rawSiteConfig.layers = sortedGroupLayers
  }

  const showElements = function () {
    // Show the menu button, fullscreen and search field buttons
    $('#drawer-show,#search-input-dummy').removeClass('hidden')

    if (fullScreenIsSupported) {
      $('#map-fullscreen-button').removeClass('hidden')
    }

    // If we are in an iframe, override the setting where the fullscreen button gets
    // hidden on small devices and always show it (provided it is supported, which it
    // currently is not on iPhones)
    const { iframe } = window.getAppInfo()

    if (iframe && fullScreenIsSupported) {
      $('#map-fullscreen-button').css('display', 'block')
    }

    // Show aerial toggle button if enabled and layer is available
    const aerialphotoVisible = Model.get('config.layout.map.buttons.aerialphoto.visible', true)
    const aerialPhotoAvailable = aerialphotoVisible && PoziUtils.getApp().poziMap.getAerialLayer() // This will either return a layer or undefined

    if (aerialPhotoAvailable) {
      const aerialphotoControlType = Model.get('config.layout.map.buttons.aerialphoto.controltype', 'button')?.toString() // or slider
      if (aerialphotoControlType === 'button') {
        $('#elt-imagery-toggle').removeClass('hidden')
      } else if (aerialphotoControlType === 'slider') {
        $('#elt-slider').removeClass('hidden')
      }
    }
  }

  const initApp = function () {
    // If state set then zoom to location.
    PoziAPISearch().init() // this is an async function but we won't wait for it

    PoziDrawer().init()

    /* PoziPropertyLookup()
      .wireup()
      .then(function (result) {
        console.log(result)
      })
      .catch(function (error) {
        console.log(error) // Probably just not configured for the site.
      }) */

    // PoziPolygonSelect().init()

    $('#map-fullscreen-button').on('click', fullScreen)

    // Update URL
    HistoryUpdate()

    // WCAGCompilance()

    app.runTests()

    if (!Model.get('app.map.initialised')) {
      console.log('!!! ERROR !!! Map was not initialised.')

      PoziUtils.gtag({
        category: 'program',
        status: 'warning',
        identifier: 'Pozi',
        action: 'Map initialisation failed.',
        additionalInfo: ''
      })
    }

    console.debug('PoziConfig - init app done.')
  }
})

export default PoziConfig
